// LIVE SELECTION: what the viewer publishes while a box is being dragged,
// and what it publishes when the drag ends.
//
// The shape is the one the server already accepts, so nothing translates
// at the seam (leader + Mark, 2026-09-24):
//   { uniqueIds?, selectionId?, nodeKeys?, recordNodes?, count, source }
// and the TRANSPORT concerns travel beside it rather than inside it:
//   { seq, inProgress, at }
//
// ABSENT IS NOT EMPTY, and this is the one decision in this file that
// someone will otherwise undo. Mid-drag the panel is told the COUNT and
// NOT the identities - Mark cannot render a table for a selection that
// is still changing, and a full list per frame would cost traffic for
// something nobody can paint. So mid-drag `uniqueIds` is OMITTED. It is
// NOT sent as an empty array, because an empty array means "nothing is
// selected" and omission means "identity has not been sent yet". Those
// are different answers and a consumer that confuses them will either
// clear a panel that should be filling or fill one that should be empty.
//
// COUNT IS THE TRUTH, uniqueIds IS WHAT FITTED. On release the whole list
// goes if it can; when it is capped, `count` still carries the REAL total
// while `uniqueIds` carries what was sent - so a consumer can say "acting
// on the first N of M" rather than quietly writing N. That needs no extra
// field: uniqueIds.length < count IS the cap, self-describing.
//
// WHAT THIS DOES NOT SAVE, said plainly because it would otherwise be
// assumed: computing the COUNT costs the same as computing the LIST -
// both walk the active instances and project each centre. The saving
// from omitting identities mid-drag is in TRANSPORT and in React state
// churn, not in the projection. The projection is made affordable by
// throttling to one frame, not by sending less.

// A monotonic sequence, so a late tick cannot land on top of a newer one.
// The drag produces a stream; on a stream this is certain rather than
// likely. (Both Mark and I hit the same late-arrival bug in a
// request-per-keystroke path the same afternoon.)
export function makePublisher(send) {
  let seq = 0;
  return {
    publish(state, { inProgress = false } = {}) {
      if (typeof send !== 'function') return null;
      seq += 1;
      const meta = { seq, inProgress, at: Date.now() };
      try { send(state, meta); } catch (e) { /* a consumer's handler must not break the drag */ }
      return meta;
    },
    get seq() { return seq; },
  };
}

// Mid-drag: the count, and deliberately no identities.
export function inProgressState(count, source = 'box') {
  return { count: Number(count) || 0, source };
}

// On release: the identities, with `count` as the true total even when
// the list was capped.
export function finalState(uniqueIds, source = 'box', trueCount = null) {
  const ids = Array.isArray(uniqueIds) ? uniqueIds : [];
  const n = Number.isFinite(Number(trueCount)) && trueCount !== null ? Number(trueCount) : ids.length;
  return { uniqueIds: ids, count: n, source };
}

// Was that list capped? Asked of the state rather than carried beside it.
export function wasCapped(state) {
  if (!state || !Array.isArray(state.uniqueIds)) return false;
  return state.uniqueIds.length < (Number(state.count) || 0);
}

// WHAT CHANGED, so a frame repaints only the difference. Repainting the
// whole set each frame is what will not survive SpaceX; per-instance
// colour is cheap, but only if the number of instances touched is the
// number that actually moved in or out of the rectangle.
export function diffSets(prev, next) {
  const added = [];
  const removed = [];
  for (const uid of next) if (!prev.has(uid)) added.push(uid);
  for (const uid of prev) if (!next.has(uid)) removed.push(uid);
  return { added, removed };
}
